import { createContext, useCallback, useContext, useMemo } from 'react';
import { keys, noop, get, set, isNil, isObject, cloneDeep, forEach } from 'lodash';
import { isEmpty } from 'lodash';
import { ModuleContext } from '../../module/ModuleProvider';
import { APP_EVENT_TYPE } from '../appEventType';

export const AppEventsContext = createContext({ register: noop, push: noop });

export const KEYS_TO_REMOVE = ['campaignTargeting.postalCodeTargeting', 'blockedSitesOnAccountLevel'];

const sanitizeData = data => {
    if (!isObject(data)) {
        return data;
    }

    const result = cloneDeep(data);

    forEach(KEYS_TO_REMOVE, path => {
        const value = get(result, path);
        if (!isNil(value)) {
            set(result, path, null);
        }
    });

    return result;
};

const transformData = (event, toGWTransformers, pathToEntity) => {
    const { eventType, data } = event || {};
    if (eventType === APP_EVENT_TYPE.CHAT_DIALOG_MESSAGE) {
        return data;
    }

    if (isEmpty(toGWTransformers) || isEmpty(pathToEntity) || isEmpty(data)) {
        return data;
    }

    return keys(data).reduce((acc, key) => {
        const entity = pathToEntity[key];
        if (entity) {
            const transformer = toGWTransformers[entity];
            if (transformer) {
                const sanitizedData = sanitizeData(data[key]);
                const trasnformedData = transformer(sanitizedData);
                acc[key] = trasnformedData;
            }
        }
        return acc;
    }, {});
};

export const useAppEventContext = () => {
    const { push, ...rest } = useContext(AppEventsContext);
    const { toGWTransformers, pathToEntity } = useContext(ModuleContext);
    const pushWrapper = useCallback(
        event => {
            const transformedData = transformData(event, toGWTransformers, pathToEntity);

            return push({
                ...event,
                data: {
                    ...event.data,
                    ...transformedData,
                },
            });
        },
        [pathToEntity, push, toGWTransformers]
    );
    const wrappedValue = useMemo(() => ({ push: pushWrapper, ...rest }), [pushWrapper, rest]);

    return wrappedValue;
};
